import { once, scheduleOnce } from '@ember/runloop';
import { observer } from '@ember/object';
import { readOnly } from '@ember/object/computed';
import { on } from '@ember/object/evented';
import { Promise } from 'rsvp';
import Service, { inject as service } from '@ember/service';
import {
  POSTING_CREATE__ASSIGNMENT_INCLUDES,
  POSTING_CREATE__CLASSIFICATION_CATEGORY_LIMIT,
  POSTING_CREATE__ERROR_CODES,
  POSTING_CREATE_COVER_EXPENSES_TYPE,
  POSTING_CREATE_DATE_RANGE_TYPE,
  POSTING_CREATE_ESTIMATED_DAY_TYPES,
  POSTING_CREATE_ESTIMATED_WHOLE_DAY_COUNT,
  POSTING_CREATE_FREELANCER_LEVEL,
  POSTING_CREATE_MIN_WHOLE_DAY_COUNT,
  POSTING_CREATE_VALID_FREE_TEXT_HTML,
} from "../../../constants/routes/posting-create";
import { CLASSIFICATION_CATEGORIES } from 'sh-www/models/classification';
import { LOCATION } from 'sh-www/models/location';
import { handleErrorResponseWithModal, showGenericErrorModal } from 'sh-www/utils/handles-errors';
import { scrollToError } from 'sh-www/utils/handles-errors-v2';
import { showModal } from 'sh-www/utils/modal-controls';
import { ASSIGNMENT } from "../../../models/assignment";
import { CLASSIFICATION_TYPES } from 'sh-www/models/classification';
import { COUNTRY } from 'sh-www/models/country';
import { REGION } from 'sh-www/models/region';
import { ASSIGNMENT_COMMAND_CONSTANTS } from "../../commands/assignment-commands";
import { BANNER_MESSAGE_TYPES } from "../../states/application";
import {
  convertNewLinesToBRs,
  htmlEntitiesDecode,
  md5Hash,
  stripTags
} from "../../../utils/misc";
import { EMBER_ROUTE_NAMES } from "../../../constants/routes";
import { createDeliverable } from 'sh-www/components/seller-postings/seller-posting-deliverables-input';
import { createScheduleItem } from 'sh-www/components/schedule-creator';

export default Service.extend({
  /**
   * Register services
   */
  applicationStateManager: service('state-managers/application'),
  assignmentManager: service('managers/assignment-manager'),
  assignmentCommandService : service('commands/assignment-commands'),
  classificationCommandService: service('commands/classification-commands'),
  companyCommandService : service('commands/company-commands'),
  companyTeamService : service('commands/company-team-commands'),
  errorTracker: service('error-tracker'),
  moment: service(),
  router: service(),
  session: service(),
  shData: service(),
  state: service('states/routes/posting-create'),
  store: service(),
  userCommandService : service('commands/user-commands'),

  currentUser: readOnly('session.currentUser'),

  /**
   * Admin only call to approve a pending assignment.
   *
   * @return {Ember.RSVP.Promise}
   */
  approveAssignment() {
    return new Promise((resolve, reject) => {
      let assignment = this.get('state.editingPosting');

      this.set('state.isApprovePostingRequestInFlight', true);

      this.get('assignmentCommandService').approveAssignment(assignment).then(() => {
        let modalModel = {
          titleText: 'Assignment Approved',
          bodyText: 'You have successfully approved the assignment',
          actionButtonText: 'Okay',
          isShowCancelButton: false,
          confirmAction: () => {
            this.toggleConfirmDiscardData(false);
            window.location.assign(`/posting/${assignment.get('idWithSlug')}`);
          }
        };

        showModal(this, 'confirmation', modalModel);

        resolve(true);
      }).catch(e => {
        handleErrorResponseWithModal(this, e);
        reject(e);
      }).finally(() => this.set('state.isApprovePostingRequestInFlight', false));
    });
  },

  /**
   * Reset the testMatches state to the default.
   *
   * @param {Boolean=} opt_isClearTestMatchesHash     Set to true clear the hashed state as well
   */
  clearTestMatchesState(opt_isClearTestMatchesHash = false) {
    this.set('state.testMatchesBudgetStatus', ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.NONE);
    this.set('state.testMatchesConfirmationStatus', ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.NONE);
    this.set('state.testMatchesRatingStatus', ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.NONE);
    this.set('state.testMatchesSummaryStatus', ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.NONE);

    this.set('state.testMatchesBudgetText', '');
    this.set('state.testMatchesConfirmationText', '');
    this.set('state.testMatchesRatingText', '');
    this.set('state.testMatchesSummaryText', '');

    if (opt_isClearTestMatchesHash) {
      this.set('state.testMatchesPreviousHash', '');
      this.set('state.testMatchesCurrentHash', '');
    }
  },

  /**
   * Create an assignment with the latest data from the state.
   *
   * @param {Boolean=} opt_isDraft
   * @param {Boolean=} opt_isConfirmTestMatches   True to confirm the testMatchess state and abort create if does not pass.
   * @return {Ember.RSVP.Promise} @see AssignmentManager.validateAndGenerateAssignmentCreateData on the error response.
   */
  createAssignment(opt_isDraft, opt_isConfirmTestMatches) {
    if (this.state.isCreatePostingRequestInFlight) {
      this.errorTracker.error('createAssignment -- create assignment while one is in flight');
      return Promise.reject(new Error('Another request to create an assignment is already in flight.'));
    }

    this.set('state.errors', {});

    const onMatchesConfirmed = () => {
      this.createAssignment(false, false);
    };

    if (opt_isConfirmTestMatches && !this._isTestMatchesConfirmed(onMatchesConfirmed)) {
      return Promise.reject(new Error('Not enough matches found.'));
    }

    this.set('state.isCreatePostingRequestInFlight', true);

    const state = this.state;
    const coverExpenses = state.updatedCoverExpenses;
    const isCoverExpenses = coverExpenses ? coverExpenses === POSTING_CREATE_COVER_EXPENSES_TYPE.YES : null;
    const isCoverCreatorFees = this.state.showSellerFeesField
      ? state.updatedCoverFees
      : state.buyerCompany.alwaysCoversCreatorFees;
    const description = stripTags(state.updatedDescription, POSTING_CREATE_VALID_FREE_TEXT_HTML);
    const examples = stripTags(state.updatedExamples, POSTING_CREATE_VALID_FREE_TEXT_HTML);
    const isUrgent = opt_isDraft ? null : state.updatedDateRangeType === POSTING_CREATE_DATE_RANGE_TYPE.WITHIN_24_HOURS;
    const regionId = state.locationModelType === LOCATION.TYPES.REGION ? state.updatedLocation.id : 0;
    const countryId = state.locationModelType === LOCATION.TYPES.COUNTRY ? state.updatedLocation.id : 0;
    const candidateCountryId = state.candidateLocationModelType === LOCATION.TYPES.COUNTRY ? state.updatedCandidateLocation.id : 0;

    const assignmentCreateData = this.assignmentManager.validateAndGenerateAssignmentCreateData(
      false,
      state.createdFrom,
      state.isSelect,
      state.isEligibleForSelect,
      state.updatedProduction?.id,
      state.updatedCompanyTeamId,
      state.updatedTitle,
      state.updatedPostingType,
      opt_isDraft,
      state.updatedScheduleItemsParsed,
      state.updatedSelectedServices,
      state.updatedClassificationIds,
      state.updatedCustomClassifications,
      opt_isDraft ? null : state.updatedDateSelected,    //don't save a date on a draft.
      description,
      state.updatedDeliverablesParsed,
      parseInt(state.updatedPrivacyLevel),
      parseInt(state.updatedBudgetLowValue),
      parseInt(state.updatedBudgetHighValue),
      parseInt(state.updatedRateType),
      this.state.showSellerFeesField,
      this.state.showExpensesField,
      true,
      isCoverCreatorFees,
      isCoverExpenses,
      state.updatedExpedite,
      false,
      state.updatedMinRating,
      isUrgent,
      state.updatedEstimatedDays,
      examples,
      state.updatedPlaceId,
      regionId,
      countryId,
      state.isGlobal,
      state.updatedCandidatePlaceId,
      candidateCountryId,
      state.isCandidateGlobal,
      state.updatedClonedAssignmentId,
      state.client_uuid
    );

    if (assignmentCreateData.hasErrors) {
      this.set('state.errors', assignmentCreateData.errorsMap);
      scrollToError(this, assignmentCreateData.errorsMap);
      this.set('state.isCreatePostingRequestInFlight', false);
      return Promise.reject(new Error('There was a problem validating assignment data.'));
    }

    return this.assignmentCommandService.createAssignment(assignmentCreateData.createAssignmentDataMap).then(response => {
      this.shData.pushResponseToStore(response);
      const assignment = this.store.peekRecord('assignment', response.data.id);

      if (opt_isDraft) {
        this.applicationStateManager.setBannerMessage(BANNER_MESSAGE_TYPES.POSTING_DRAFT_SUCCESS, true);
        this.state.onDraftSuccessNavigation(assignment);
        return assignment;
      }

      if (!this.currentUser.company.approved) {
        this.applicationStateManager.setBannerMessage(BANNER_MESSAGE_TYPES.POSTING_SUBMISSION_SUCCESS, true);
        this.applicationStateManager.setNextRoute(EMBER_ROUTE_NAMES.POSTING__PENDING, null, assignment.idWithSlug);
        return assignment;
      }

      if (assignment.select) {
        this.applicationStateManager.setBannerMessage(BANNER_MESSAGE_TYPES.POSTING_SUBMISSION_SUCCESS_SELECT, true);
        this.applicationStateManager.setNextRoute(EMBER_ROUTE_NAMES.POSTING__PENDING, null, assignment.idWithSlug);
        return assignment;
      }

      this.toggleConfirmDiscardData(false);
      window.location.assign(`/posting/${assignment.slug}?newly_published=1&created-work&goal=assignment_posted&view_type=legacy`);
      return assignment;
    }).catch(e => {
      this.set('state.isCreatePostingRequestInFlight', false);

      let errors = e.errors || null;

      this.set('state.errors', errors);

      if (errors) {
        scrollToError(this, errors);
      }
    });
  },

  /**
   * Validate the direct hire overview section (title, services, location, and message).
   *
   * @returns {Boolean} Whether the direct hire overview inputs are valid.
   */
  isDirectHireOverviewValid() {
    this.set('state.errors', {});

    // Validate the fields in the overview section.
    const validationResults = this.get('assignmentManager').validateDirectHireOverview(
      this.get('state.updatedTitle'),
      this.get('state.updatedSelectedServices'),
      this.get('state.updatedPlaceId'),
      this.get('state.isGlobal'),
      this.get('state.updatedDirectFreelancerMessage'),
      this.state.updatedDeliverablesParsed,
    );

    // If there are errors, update the state and return false.
    if (validationResults.hasErrors) {
      this.set('state.errors', validationResults.errorsMap);
      scrollToError(this, validationResults.errorsMap);
      return false;
    }

    return true;
  },

  /**
   * Validate the direct hire offer section.
   *
   * @returns {Boolean} Whether the direct hire overview inputs are valid.
   */
  isDirectHireOfferValid() {
    this.set('state.errors', {});

    const coverExpenses = this.get('state.updatedCoverExpenses');
    let willCoverExpenses = null;

    if (coverExpenses) {
      willCoverExpenses = coverExpenses === POSTING_CREATE_COVER_EXPENSES_TYPE.YES;
    }

    // Validate the fields in the offer section.
    const validationResults = this.get('assignmentManager').validateDirectHireOffer(
      this.get('state.updatedDirectOfferAmount'),
      this.get('state.showExpensesField'),
      willCoverExpenses,
      this.state.showSellerFeesFieldProject,
      this.state.updatedCoverSellerFees ?? null,
      true,
      this.state.updatedExpedite ?? null,
    );

    // If there are errors, update the state and return false.
    if (validationResults.hasErrors) {
      this.set('state.errors', validationResults.errorsMap);
      scrollToError(this, validationResults.errorsMap);
      return false;
    }

    return true;
  },

  /**
   * Create a direct hire assignment with a freelancer
   *
   * @return {Ember.RSVP.Promise}
   */
  createDirectHireAssignment() {
    if (this.state.isCreatePostingRequestInFlight) {
      this.errorTracker.error('createDirectHireAssignment -- create assignment while one is in flight');
      return Promise.reject(new Error('Another request to create an assignment is already in flight.'));
    }

    this.set('state.errors', {});
    this.set('state.isCreatePostingRequestInFlight', true);

    const state = this.state;
    const allCostCodes = this.store.peekAll('costCode');
    let storyCodeId = state.updatedDirectStoryCode;
    let costCodeId = state.updatedDirectCostCode;
    const coverExpenses = state.updatedCoverExpenses;
    const isCoverExpenses = coverExpenses ? coverExpenses === POSTING_CREATE_COVER_EXPENSES_TYPE.YES : null;
    const isExpedite = state.updatedExpedite ?? null;
    const coverSellerFees = state.updatedCoverSellerFees ?? null;
    const isCoverSellerFees = state.buyerCompany.alwaysCoversCreatorFees || coverSellerFees;
    const freelancerUser = this.state.updatedDirectFreelancerUser;
    const direct = state.updatedDirectIsWorkComplete ?
        ASSIGNMENT_COMMAND_CONSTANTS.DIRECT.DIRECT_COMPLETE :
        ASSIGNMENT_COMMAND_CONSTANTS.DIRECT.DIRECT_NOT_COMPLETE;

    const assignmentCreateData = this.assignmentManager.validateAndGenerateDirectHireAssignmentCreateData(
      state.updatedTitle,
      direct,
      state.updatedProduction?.id,
      state.updatedCompanyTeamId,
      freelancerUser.id,
      state.updatedDirectIsReadyToMakeOffer,
      state.updatedDirectIsWorkComplete,
      state.updatedSelectedServices,
      state.updatedDeliverablesParsed,
      state.updatedScheduleItemsParsed,
      state.updatedDirectFreelancerMessage,
      this.state.showExpensesField,
      true,
      isCoverExpenses,
      isExpedite,
      this.state.showSellerFeesFieldProject,
      isCoverSellerFees,
      state.updatedPlaceId,
      state.isGlobal,
      costCodeId ? costCodeId : '',
      storyCodeId ? storyCodeId : '',
      state.updatedDirectReviewComment,
      state.updatedDirectReviewStarCount,
      state.updatedDirectOfferAmount,
      state.client_uuid
    );

    if (assignmentCreateData.hasErrors) {
      this.set('state.errors', assignmentCreateData.errorsMap);
      this.set('state.isCreatePostingRequestInFlight', false);
      scrollToError(this, assignmentCreateData.errorsMap);
      return Promise.reject(new Error('There was a problem validating assignment data.'));
    }

    return this.assignmentCommandService.createAssignment(assignmentCreateData.createAssignmentDataMap, ['applications']).then(response => {
      this.shData.pushResponseToStore(response);
      const assignment = this.store.peekRecord('assignment', response.data.id);
      const meta = response.meta;

      const application = assignment.applications.objectAt(0);

      this.applicationStateManager.addToast('Project created');

      this.toggleConfirmDiscardData(false);
      if (state.updatedDirectIsWorkComplete) {
        const transition = this.applicationStateManager.setNextRoute(
          EMBER_ROUTE_NAMES.PROJECT__COMPLETE__PRIVATE_FEEDBACK,
          null,
          application.id);

        if (meta.hasOwnProperty('buyer_pay_fail')) {
          transition.data.errors = { buyerPayFail: meta.buyer_pay_fail };
        }
      } else {
        this.applicationStateManager.setNextRoute(
          EMBER_ROUTE_NAMES.PROJECT__DETAILS,
          null,
          application.id);
      }

      return assignment;
    }).catch(e => {
      const errors = e.errors || null;

      this.set('state.errors', errors);

      if (errors) {
        scrollToError(this, errors);
      }

      this.set('state.isCreatePostingRequestInFlight', false);
    });
  },

  /**
   * Decline the posting and update state.
   */
  declinePosting() {
    let modalModel = {
      title: `Are you sure you want to decline this posting?`,
      textAreaPlaceholder: 'Enter optional text for the user ...',
      cancelText: 'Cancel',
      confirmText: 'Decline',
      onConfirmMessage: (message) => {
        this.set('state.isDeclinePostingRequestInFlight', true);

        this.get('assignmentCommandService').declineAssignment(
          this.get('state.editingPosting'),
          message,
          POSTING_CREATE__ASSIGNMENT_INCLUDES
        ).then(assignment => {
          this.get('applicationStateManager').addToast('Posting Declined');
          this._updateStateFromExistingAssignment(assignment);
        }).catch(() => {
          // gobble gobble
        }).finally(() => this.set('state.isDeclinePostingRequestInFlight', false));
      }
    };

    showModal(this, 'lsg/lsg-modal-text-editor', modalModel);
  },

  /**
   * Delete an assignment and update our state.
   *
   * @return {Ember.RSVP.Promise}
   */
  deleteAssignment() {
    if (this.get('state.isEditPostingRequestInFlight')) {
      this.get('errorTracker').error('deleteAssignment -- update assignment while one is in flight');
      return Promise.reject(false);
    }

    this.set('state.isEditPostingRequestInFlight', true);
    this.set('state.errors', {});

    let assignment = this.get('state.editingPosting');

    return new Promise((resolve, reject) => {
      this.get('assignmentCommandService').deleteAssignment(assignment).then(() => {
        this.toggleConfirmDiscardData(false);
        resolve(true);
      }).catch(e => {
        let errors = e.errors || null;
        this.set('state.errors', errors);
        if (errors) {
          scrollToError(this, errors);
        }

        reject(e);
      }).finally(() => {
        this.set('state.isEditPostingRequestInFlight', false);
      });
    });
  },

  /**
   * Handle the story codes were cancelled by the user.
   */
  handleStoryCodesCancelled() {
    this.set('state.updatedDirectStoryCode', '');
  },

  /**
   * Update the current hash for testMatches state.
   */
  updateCurrentHashForTestMatches() {
    this.set('state.testMatchesCurrentHash', this._getTestMatchesHash());
  },

  /**
   * Update the previous hash for the testMatches state.
   */
  updatePreviousHashForTestMatches() {
    this.set('state.testMatchesPreviousHash', this._getTestMatchesHash());
  },

  /**
   * Initialize our state for a new assignment creation.
   *
   * This will NOT clear existing items that should be static b/t assignments (e.g. countries, regions, and services)
   */
  initStateManager() {
    this.get('state').init();
  },

  /**
   * Load an assignment id, or pull it out of the cache.
   *
   * @param {String} assignmentId
   * @param {Boolean} opt_isUpdateAssignmentState   True if you want to set the assignment state after loading.
   * @return {Ember.RSVP.Promise}
   */
  loadAssignment(assignmentId, opt_isUpdateAssignmentState = true) {
    const includes = POSTING_CREATE__ASSIGNMENT_INCLUDES;
    return this.assignmentCommandService.loadAssignment(assignmentId, includes).then(assignment => {
      if (opt_isUpdateAssignmentState) {
        this._updateStateFromExistingAssignment(assignment, true);
      }

      return assignment;
    }).catch((e) => {
      this.get('errorTracker').error('loadAssignment -- error loading assignment', this, e);
    });
  },

  /**
   * @param {String} assignmentId     Load the cloned assignment ID and set up the state.
   * @return {Ember.RSVP.Promise}
   */
  loadClonedAssignment(assignmentId) {
    if (!assignmentId) {
      // if no assignment id, just resolve true.  no work to do.
      return Promise.resolve(true);
    }

    return new Promise((resolve, reject) => {
      this.loadAssignment(assignmentId, false).then(assignment => {
        this._updateStateFromExistingAssignment(assignment, false);

        this.set('state.updatedClonedAssignmentId', assignmentId);
        this.set('state.editingPosting', null);

        resolve(assignment);
      }).catch(e => {
        this.get('errorTracker').error('loadClonedAssignment -- failed loading cloned assignment', this, e);
        reject(e);
      });
    });
  },

  /**
   * Load the current user's company and include the story/cost codes
   *
   * @return {Ember.RSVP.Promise}
   */
  loadCompany() {
    return new Promise((resolve, reject) => {
      this.get('companyCommandService').loadCompany(this.get('currentUser.company.id'), [
        'cost-codes'
      ]).then(company => {
        resolve(company);
      }).catch(e => {
        reject(e);
      });
    });
  },

  /**
   * Loads the members of the company and invitees.
   *
   * @param {Number} companyId
   * @return {Ember.RSVP.Promise}
   */
  loadCompanyWithMembers(companyId) {
    const memberIncludes = [
      'company-memberships.user',
      'company-memberships.company-team-memberships',
      'cost-code.company-teams',
    ];

     return this.companyCommandService.loadCompany(companyId, memberIncludes)
      .then((company) => {
        this.set('state.companyMemberships', company.companyMemberships);
        return company.companyMemberships;
      });
  },

  /**
   * Load the company teams.
   *
   * @param {Number} companyId
   * @return {Ember.RSVP.Promise}
   */
   loadCompanyTeams(companyId) {
    const teamIncludes = [
      'company-team-memberships.company-membership.user',
    ];

    return this.companyTeamService.loadCompanyTeams(companyId, teamIncludes)
      .then((response) => {
        this.shData.pushResponseToStore(response);
        const companyTeams = this.shData.getModelArrayFromResponse(response, 'companyTeam');
        this.set('state.availableCompanyTeams', companyTeams);
        return companyTeams;
      });
  },

  /**
   * Load the classifications
   *
   * @return {Promise}
   */
  loadClassifications() {
    return this.classificationCommandService.loadClassifications({
      filters: {
        types: [
          CLASSIFICATION_TYPES.TYPE_DEFAULT,
        ],
        categories: [
          CLASSIFICATION_CATEGORIES.CATEGORY_SUBJECT,
          CLASSIFICATION_CATEGORIES.CATEGORY_INDUSTRY,
        ],
      },
    }).then((classifications) => {
      this.set('state.availableClassifications', classifications);
      return classifications;
    });
  },

  /**
   * Load the countries if they aren't already in memory.
   *
   * @return {Ember.RSVP.Promise}
   */
  loadCountries() {
    return this.get('store').findAll('country', {
      adapterOptions : {
        isPublic : true
      }
    }).then(countries => {
      this.set('state.availableCountries', countries);
    });
  },

  /**
   * Load the regions if they aren't already in memory.
   *
   * @return {Ember.RSVP.Promise}
   */
  loadRegions() {
    return this.get('store').findAll('region', {
      adapterOptions : {
        include : 'countries',
        isPublic : true
      }
    }).then(regions => this.set('state.availableRegions', regions));
  },

  /**
   * Load the freelancer user if this is a direct hire.
   *
   * @return {Ember.RSVP.Promise}
   */
  loadFreelancer() {
    let freelancerId = this.get('state.updatedDirectFreelancerId'),
      includes = [
        'location',
        `company-seller-connections.company:in_network_or_extended(${this.currentUser.company.id})`,
        'hiring-fee-structure',
      ];

    return new Promise((resolve, reject) => {
      this.get('userCommandService').getUser(freelancerId, includes).then(user => {
        this.set('state.updatedDirectFreelancerUser', user);
        resolve(user);
      }).catch(e => {
        this.get('errorTracker').error('loadFreelancer - could not load freelancer', e, this);
        reject(e);
      });
    });
  },

  /**
   * Load all the services from the DB.
   *
   * @return Ember.RSVP.Promise<Ember.MutableArray<Service>>
   */
  loadServices() {
    const availableServices = this.state.availableServices;
    if (availableServices.length) {
      return Promise.resolve(availableServices);
    }

    return this.store.findAll('service', {
      include: 'service-synonyms',
      adapterOptions : {
        isPublic : true
      }
    }).then(services => {
      this.set('state.availableServices', services);
      return services;
    }).catch(e => {
      this.errorTracker.error('loadServices - could not load services', this, e);
      throw e;
    });
  },

  /**
   * Publish an assignment that is currently in the draft state.
   *
   * @return {*|Ember.RSVP.Promise}
   */
  publishDraftAssignment(opt_isConfirmTestMatches) {
    let assignment = this.get('state.editingPosting');
    if (!assignment || assignment.get('status') !== ASSIGNMENT.STATUS.DRAFT) {
      let e = new Error('Can not publish a non-draft or empty assignment');
      showGenericErrorModal(this, { e });
      return Promise.reject(e);
    }

    this.set('state.isMakingDraftAssignmentLive', true);

    let onMatchesConfirmed = () => {
      this.publishDraftAssignment();
    };

    return this.updateAssignment(opt_isConfirmTestMatches, onMatchesConfirmed, false).catch(e => {
      if (e === POSTING_CREATE__ERROR_CODES.ERROR_NEEDS_TEST_MATCHES_CONFIRM) {
        return;
      }

      this.get('errorTracker').error('onPublishPostingClicked -- error saving', this, e);
    }).finally(() => this.set('state.isMakingDraftAssignmentLive', false));
  },

  /**
   * Register a success function to navigate the user on successful saving of a NEW draft.
   *
   * @param {Function} navigationCallback
   */
  registerOnDraftSuccessNavigation(navigationCallback) {
    this.set('state.onDraftSuccessNavigation', navigationCallback);
  },

  /**
   * Save a draft for the user.
   *
   * @return {Ember.RSVP.Promise}
   */
  saveDraft() {
    if (this.get('state.editingPosting')) {
      return this.updateAssignment(false, undefined, true);
    }

    return this.createAssignment(true);
  },

  /**
   * Sets the createdFrom value
   * @param createdFrom
   */
  setCreatedFrom(createdFrom) {
    this.set('state.createdFrom', createdFrom);
  },

  /**
   * update the posting type that we are creating.
   *
   * @param {Number} postingType    Assignment.ASSIGNMENT_TYPE_*
   */
  setPostingType(postingType) {
    this.set('state.updatedPostingType', postingType);
  },

  /**
   * @param {Boolean} isSelect Whether this is a Select posting.
   */
  setSelect(isSelect) {
    this.set('state.isSelect', isSelect);
  },

  /**
   * @param {string} message
   */
  setDirectFreelancerMessage(message) {
    this.set('state.updatedDirectFreelancerMessage', message);
  },

  /**
   * Call the api to test the matches we would get give the current state of the create assignment form.
   *
   * @return {Ember.RSVP.Promise}
   */
  testMatches() {
    if (this.get('state.isTestMatchesRequestInFlight')) {
      return Promise.reject(false);
    }

    this.set('state.isTestMatchesRequestInFlight', true);

    return new Promise((resolve, reject) => {
      let state = this.get('state'),
        serviceIds = state.get('updatedSelectedServices')
          .reduce((serviceIdsArr, service) => {
            serviceIdsArr.push(service.get('id'));
            return serviceIdsArr;
          }, []),
        params = {
          [ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.DEADLINE] : state.get('updatedDateSelected'),
          [ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.SERVICES] : serviceIds,
          [ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.TYPE] : state.get('updatedPostingType'),
          [ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.PRIVACY_LEVEL] : state.get('updatedPrivacyLevel'),
          [ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.COMPENSATION_MIN] : state.get('updatedBudgetLowValue'),
          [ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.COMPENSATION_MAX] : state.get('updatedBudgetHighValue'),
          [ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.MIN_RATING] : state.get('updatedMinRating'),
          [ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.ESTIMATED_DAYS] : state.get('updatedEstimatedDays'),
          [ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.RATE_TYPE] : state.get('updatedRateType'),
        };

      // set at most one location
      const { updatedLocation } = state;
      if (updatedLocation) {
        const { locationModelType } = state;

        if (locationModelType === LOCATION.TYPES.GLOBAL) {
          params[ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.GLOBAL] = true;
        } else if (locationModelType === LOCATION.TYPES.REGION) {
          params[ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.REGION] = updatedLocation.id;
        } else if (locationModelType === LOCATION.TYPES.COUNTRY) {
          params[ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.COUNTRY] = updatedLocation.id;
        } else {
          params[ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.PLACE] = updatedLocation.placeId;
        }
      }

      // set at most one location
      const { updatedCandidateLocation } = state;
      if (updatedCandidateLocation) {
        const { candidateLocationModelType } = state;

        if (candidateLocationModelType === LOCATION.TYPES.GLOBAL) {
          params[ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.CANDIDATE_GLOBAL] = true;
        } else if (candidateLocationModelType === LOCATION.TYPES.COUNTRY) {
          params[ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.CANDIDATE_COUNTRY] = updatedCandidateLocation.id;
        } else {
          params[ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_PARAMS.CANDIDATE_PLACE] = updatedCandidateLocation.placeId;
        }
      }

      this.updatePreviousHashForTestMatches();

      this.get('assignmentCommandService').testAssignmentMatches(
        params,
        md5Hash(this.get('state.testMatchesCurrentHash'))
      ).then(assignmentBlastTest => {
        this.set('state.testMatchesBudgetStatus', assignmentBlastTest.get('budget.status'));
        this.set('state.testMatchesConfirmationStatus', assignmentBlastTest.get('confirmation.status'));
        this.set('state.testMatchesRatingStatus', assignmentBlastTest.get('rating.status'));
        this.set('state.testMatchesSummaryStatus', assignmentBlastTest.get('summary.status'));

        this.set('state.testMatchesBudgetText', assignmentBlastTest.get('budget.text'));
        this.set('state.testMatchesConfirmationText', assignmentBlastTest.get('confirmation.text'));
        this.set('state.testMatchesRatingText', assignmentBlastTest.get('rating.text'));
        this.set('state.testMatchesSummaryText', assignmentBlastTest.get('summary.text'));

        resolve(assignmentBlastTest);
      }).catch(e => {
        // logged in command
        reject(e);
      }).finally(() => this.set('state.isTestMatchesRequestInFlight', false));
    });
  },

  /**
   * Set the state correctly when the user toggles using candidate location.
   *
   * @param {Boolean}   useCandidateLocation     Whether to show or hide field.
   */
  toggleUseCandidateLocation(useCandidateLocation) {
    const globalLocation = {
      locationType: LOCATION.TYPES.GLOBAL,
    };

    this.setProperties({
      'state.usingCandidateLocation': useCandidateLocation,
      'state.updatedCandidateLocation': useCandidateLocation ? globalLocation : null,
    });
  },

  /**
   * Set the state correctly when the user confirms they want to leave.
   *
   * @param {Boolean}   isConfirm     True if we want to confirm that the user will discard data.
   */
  toggleConfirmDiscardData(isConfirm) {
    this.set('state.isConfirmDiscardData', isConfirm);
  },

  /**
   * Update an existing assignment with the data in our state.
   *
   * @param {Boolean=} opt_isConfirmTestMatches     Set to true to show testMatches confirmations.
   * @param {Function=} opt_onMatchesConfirmed      A function to be called if the user sees a popover for confirm matches and
   *                                                clicks confirm.
   * @oaram {Boolean=} opt_forceDraft               True or false to force the draft status
   *
   * @return {Ember.RSVP.Promise}
   */
  updateAssignment(opt_isConfirmTestMatches, opt_onMatchesConfirmed, opt_forceDraft) {
    if (this.state.isEditPostingRequestInFlight) {
      this.errorTracker.error('updateAssignment -- update assignment while one is in flight');
      return Promise.reject(false);
    }

    const onMatchesConfirmed = opt_onMatchesConfirmed ?? (() => { this.updateAssignment(); });

    if (opt_isConfirmTestMatches && !this._isTestMatchesConfirmed(onMatchesConfirmed)) {
      return Promise.reject(POSTING_CREATE__ERROR_CODES.ERROR_NEEDS_TEST_MATCHES_CONFIRM);
    }

    this.set('state.isEditPostingRequestInFlight', true);
    this.set('state.errors', {});

    const assignment = this.state.editingPosting;

    const isDraft = opt_forceDraft ?? assignment.status === ASSIGNMENT.STATUS.DRAFT;

    const state = this.state;
    const coverExpenses = state.updatedCoverExpenses;
    const isCoverExpenses = coverExpenses ? coverExpenses === POSTING_CREATE_COVER_EXPENSES_TYPE.YES : null;
    const isCoverCreatorFees = this.state.showSellerFeesField
      ? state.updatedCoverFees
      : state.buyerCompany.alwaysCoversCreatorFees;
    const isUrgent = isDraft ? null : state.updatedDateRangeType === POSTING_CREATE_DATE_RANGE_TYPE.WITHIN_24_HOURS;
    const regionId = state.locationModelType === LOCATION.TYPES.REGION ? state.updatedLocation.id : 0;
    const countryId = state.locationModelType === LOCATION.TYPES.COUNTRY ? state.updatedLocation.id : 0;
    const candidateCountryId = state.candidateLocationModelType === LOCATION.TYPES.COUNTRY ? state.updatedCandidateLocation.id : 0;

    const assignmentUpdateData = this.assignmentManager.validateAndGenerateAssignmentCreateData(
      true,
      assignment.createdFrom, // this should never change
      state.isSelect,
      state.isEligibleForSelect,
      state.updatedProduction?.id,
      state.updatedCompanyTeam?.id ?? null,
      state.updatedTitle,
      state.updatedPostingType,
      isDraft,
      state.updatedScheduleItemsParsed,
      state.updatedSelectedServices,
      state.updatedClassificationIds,
      state.updatedCustomClassifications,
      state.updatedDateSelected,
      state.updatedDescription,
      state.updatedDeliverablesParsed,
      state.updatedPrivacyLevel,
      parseInt(state.updatedBudgetLowValue),
      parseInt(state.updatedBudgetHighValue),
      state.updatedRateTypeParsed,
      state.showSellerFeesField,
      state.showExpensesField,
      true,
      isCoverCreatorFees,
      isCoverExpenses,
      state.updatedExpedite,
      assignment.isDirect,
      state.updatedMinRating,
      isUrgent,
      state.updatedEstimatedDays,
      state.updatedExamples,
      state.updatedPlaceId,
      regionId,
      countryId,
      state.isGlobal,
      state.updatedCandidatePlaceId,
      candidateCountryId,
      state.isCandidateGlobal,
      state.updatedClonedAssignmentId);

    if (assignmentUpdateData.hasErrors) {
      this.set('state.errors', assignmentUpdateData.errorsMap);
      scrollToError(this, assignmentUpdateData.errorsMap);

      this.set('state.isEditPostingRequestInFlight', false);
      return Promise.reject(assignmentUpdateData);
    }

    return this.assignmentCommandService.updateAssignment(
      assignment,
      assignmentUpdateData.createAssignmentDataMap,
      POSTING_CREATE__ASSIGNMENT_INCLUDES
    ).then(() => {
      this._updateStateFromExistingAssignment(assignment);

      if (isDraft) {
        this.applicationStateManager.setBannerMessage(BANNER_MESSAGE_TYPES.POSTING_DRAFT_SUCCESS, true);
        this.router.transitionTo(EMBER_ROUTE_NAMES.POSTING__PENDING, this.state.editingPosting.idWithSlug);
        return assignment;
      } else if (this.state.isMakingDraftAssignmentLive) {
        this.set('state.isMakingDraftAssignmentLive', false);

        if (!this.currentUser.company.approved) {
          this.applicationStateManager.setBannerMessage(BANNER_MESSAGE_TYPES.POSTING_SUBMISSION_SUCCESS, true);
          this.router.transitionTo(EMBER_ROUTE_NAMES.POSTING__PENDING, assignment.idWithSlug);
          return assignment;
        }

        if (assignment.select) {
          this.applicationStateManager.setBannerMessage(BANNER_MESSAGE_TYPES.POSTING_SUBMISSION_SUCCESS_SELECT, true);
          this.router.transitionTo(EMBER_ROUTE_NAMES.POSTING__PENDING, assignment.idWithSlug);
          return assignment;
        }

        this.toggleConfirmDiscardData(false);
        window.location.assign(`/posting/${assignment.slug}?newly_published=1&created-work&goal=assignment_posted&view_type=legacy`);

        return assignment;
      }

      if (this.currentUser.isAdmin) {
        this.applicationStateManager.addToast('Posting Updated');
        this.router.transitionTo(EMBER_ROUTE_NAMES.POSTING__INDEX, assignment.idWithSlug);
        return assignment;
      }

      this.router.transitionTo(EMBER_ROUTE_NAMES.POSTING__PENDING, assignment.idWithSlug);
      return assignment;
    }).catch(e => {
      let errors = e.errors || null;

      this.set('state.errors', errors);

      if (errors) {
        scrollToError(this, errors);
      }

      throw e;
    }).finally(() => this.set('state.isEditPostingRequestInFlight', false));
  },

  /**
   * Update the cover seller fees state
   *
   * @param {Boolean} coverSellerFees
   */
  updateCoverSellerFees(coverSellerFees) {
    this.set('state.updatedCoverSellerFees', coverSellerFees);
  },

  /**
   * Update the cover expenses state based on the enum type.
   *
   * @param {String} coverExpensesType    of type POSTING_CREATE_COVER_EXPENSES_TYPE.*
   */
  updateCoverExpenses(coverExpensesType) {
    this.set('state.updatedCoverExpenses', coverExpensesType);
  },

  /**
   * Update the cover fees state based on the enum type.
   *
   * @param {String} coverExpensesType    of type POSTING_CREATE_COVER_FEES_TYPE.*
   */
  updateCoverFees(coverFeesType) {
    this.set('state.updatedCoverFees', coverFeesType);
  },

  /**
   * Update the cover fees state based on the enum type.
   *
   * @param {String} expediteType    of type POSTING_CREATE_EXPEDITE_TYPE.*
   */
  updateExpedite(expediteType) {
    this.set('state.updatedExpedite', expediteType);
  },

  /**
   * Update the date range type enum
   *
   * @param {String} dateRangeType      of type POSTING_CREATE_DATE_RANGE_TYPE.*
   */
  updateDateRangeType(dateRangeType) {
    this.set('state.updatedDateRangeType', dateRangeType);

    if (dateRangeType === POSTING_CREATE_DATE_RANGE_TYPE.WITHIN_24_HOURS) {
      let oneDayLater = this.get('moment').moment().add(1, 'day');
      this.updateDeadlineDate(oneDayLater);
    }
  },

  /**
   * Update the deadline date
   *
   * @param {Moment=} opt_momentDateSelected      Leave empty to clear the selection.
   */
  updateDeadlineDate(opt_momentDateSelected) {
    if (opt_momentDateSelected) {
      opt_momentDateSelected.hours(23).minutes(59).seconds(59).milliseconds(0);
    }

    this.set('state.updatedDateSelected', opt_momentDateSelected);
  },

  /**
   * Update the offer amount on direct project
   *
   * @param {Number} updatedDirectOfferAmount      Offer amount in USD.
   */
  updateDirectOfferAmount(updatedDirectOfferAmount) {
    this.set('state.updatedDirectOfferAmount', updatedDirectOfferAmount);
  },

  /**
   * Update the assignment deliverables
   *
   * @param {Array<AssignmentDeliverable>} deliverables
   */
  updateAssignmentDeliverables(deliverables) {
    this.set('state.updatedDeliverables', deliverables);
  },

  /**
   * Update the estimated days from the enum passed in.
   *
   * @param {String} estimatedDayTypeEnum     of type POSTING_CREATE_ESTIMATED_DAY_TYPES.*
   */
  updateEstimatedDays(estimatedDayTypeEnum) {
    this.set('state.updatedEstimatedDaysTypeEnum', estimatedDayTypeEnum);

    if (estimatedDayTypeEnum !== POSTING_CREATE_ESTIMATED_DAY_TYPES.CUSTOM_NUMBER_OF_DAYS) {
      this.set('state.updatedEstimatedCustomDays', '');
    }
  },

  /**
   * Update our state for explictly showing the examples input.
   *
   * @param {boolean} show
   */
  updateExamplesExplictlyShown(show) {
    this.set('state.updatedExamplesExplictlyShown', show);
  },

  /**
   * Update our state for the direct hire mode.
   *
   * @param {String} freelancerId
   */
  updateForDirectHire(freelancerId) {
    this.set('state.updatedDirectFreelancerId', freelancerId);
  },

  /**
   * Update the freelancer level in our state based on the enum.
   *
   * @param {String} freelancerLevel    of type POSTING_CREATE_FREELANCER_LEVEL.*
   */
  updateFreelancerLevel(freelancerLevel) {
    this.set('state.updatedFreelancerLevel', freelancerLevel);
  },

  updateHasKeyDates(hasKeyDates) {
    this.set('state.updatedHasKeyDates', hasKeyDates);
  },

  updateDirectCostCode(costCodeId) {
    this.set('state.updatedDirectCostCode', costCodeId);
  },

  updateDirectStoryCode(storyCodeId) {
    this.set('state.updatedDirectStoryCode', storyCodeId);
  },

  updateDirectReviewComment(text) {
    this.set('state.updatedDirectReviewComment', text);
  },

  /**
   * @param {Boolean} isWorkComplete    Set if the work has been complete or not.
   */
  updateIsWorkComplete(isWorkComplete) {
    if (!isWorkComplete) {
      this.set('state.updatedDirectCostCode', '');
      this.set('state.updatedDirectStoryCode', '');
      this.set('state.updatedDirectReviewStarCount', 0);
      this.set('state.updatedDirectReviewComment', '');
    }

    this.set('state.updatedDirectIsWorkComplete', isWorkComplete);
  },

  /**
   * Update the privacy level as selected by the user.
   *
   * @param {String|Number} privacyLevel    of type Assignment.PRIVACY_LEVEL* might need to be parsed to int
   */
  updatePrivacyLevel(privacyLevel) {
    privacyLevel = parseInt(privacyLevel);
    if (privacyLevel !== ASSIGNMENT.PRIVACY_LEVEL.PUBLIC) {
      this.clearTestMatchesState(true);
    }

    this.set('state.updatedPrivacyLevel', privacyLevel);
  },

  /**
   * Update our state about whether the user is ready to make an offer in direct hire mode.
   *
   * @param {Boolean} isReadyToMakeOffer
   */
  updateReadyToMakeOffer(isReadyToMakeOffer) {
    if (!isReadyToMakeOffer) {
      this.set('state.updatedDirectOfferAmount', null);

      if (this.get('state.updatedDirectIsWorkComplete')) {
        this.updateIsWorkComplete(false);
      }
    }

    this.set('state.updatedDirectIsReadyToMakeOffer', isReadyToMakeOffer);
  },

  /**
   * Set the rating that the buyer has given the seller in the direct hire flow.
   *
   * @param {Number} starCount
   */
  updateReviewStarCount(starCount) {
    this.set('state.updatedDirectReviewStarCount', starCount);
  },

  /**
   * Update the selected countries that the user has picked.
   *
   * @param {Ember.MutableArray<Object>} countryOptions   The options for the multi-select that the user has selcted.
   * @param {Boolean=} opt_isReplace    True if you want to replace all selected.
   */
  updateSelectedCountries(countryOptions, opt_isReplace) {
    let updatedCountryOptions = this.get('state.updatedCountryOptions');
    if (opt_isReplace) {
      updatedCountryOptions.clear();
    }

    updatedCountryOptions.addObjects(countryOptions);
    this.updateCityLocation(null);
  },

  removeSelectedCountries(countryOption) {
    this.state.updatedCountryOptions.removeObject(countryOption);
  },

  /**
   * Update the assignment schedule
   *
   * @param {Array<AssignmentSchedule>} schedule
   */
  updateAssignmentScheduleItems(schedule) {
    this.set('state.updatedScheduleItems', schedule);
  },

  /**
   * Get a hashed string for the testMatches state.
   *
   * This is mainly used to determine if we need to call the API again to check test matches because the user has
   * changed something important.
   *
   * @return {String}
   */
  _getTestMatchesHash() {
    let services = this.get('state.updatedSelectedServices'),
      postingType = this.get('state.updatedPostingType'),
      privacyLevel = this.get('state.updatedPrivacyLevel'),
      budgetLow = this.get('state.updatedBudgetLowValue'),
      budgetHigh = this.get('state.updatedBudgetHighValue'),
      freelancerLevel = this.get('state.updatedFreelancerLevel'),
      minRating = this.get('state.updatedMinRating'),
      estimatedDays = this.get('state.updatedEstimatedDays'),
      rateType = this.get('state.updatedRateType'),
      location = this.get('state.updatedLocation'),
      candidateLocation = this.get('state.updatedCandidateLocation');

    const servicesString = services.reduce((carry, service) => carry + service.get('id'), '');
    const locationString = location?.placeId ?? '';
    const candidateLocationString = candidateLocation?.placeId ?? '';

    return `${freelancerLevel}-${servicesString}-${postingType}-${postingType}-${privacyLevel}-${budgetLow}-${budgetHigh}-${minRating}-${estimatedDays}-${rateType}-${locationString}-${candidateLocationString}`;
  },

  /**
   * Show a confirmation popover to the user in the case where we have not passed the testMatches state test.
   *
   * @param {Function} onMatchesConfirmed
   * @return {boolean}    True if we have passed the testMatches state test, and false if we have prompted user to confirm.
   */
  _isTestMatchesConfirmed(onMatchesConfirmed) {
    let testMatchesConfirmationStatus = this.get('state.testMatchesConfirmationStatus'),
      testMatchesSummaryStatus = this.get('state.testMatchesSummaryStatus');
    if (testMatchesSummaryStatus === ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.GOOD &&
    testMatchesConfirmationStatus === ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.GOOD) {
      return true;
    }

    let bodyText = convertNewLinesToBRs(this.get('state.testMatchesConfirmationText')),
      titleText = 'Submit posting with no matches?';

    if (testMatchesSummaryStatus === ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.BLOCK) {
      bodyText = this.get('state.testMatchesSummaryText');
      let modalModel = {
        titleText: 'You cannot submit this assignment.',
        bodyText: bodyText,
        actionButtonText: 'Adjust Posting',
        isShowCancelButton: false
      };

      showModal(this, 'confirmation', modalModel);
      return false;
    }

    if (testMatchesConfirmationStatus !== ASSIGNMENT_COMMAND_CONSTANTS.TEST_MATCHES_STATUS.WARN) {
      return true;
    }

    let modalModel = {
      titleText: titleText,
      bodyText: bodyText,
      actionButtonText: 'Adjust Posting',
      cancelButtonText: 'Submit Posting',
      cancelAction: () => {
        onMatchesConfirmed();
      },
      isShowCancelButton: true
    };

    showModal(this, 'confirmation', modalModel);
    return false;
  },

  /**
   * Observe the test matches state and run a test at the right times after the user has entered enough data.
   */
  _observeTestMatches: on('init', observer(
  'state.isNeedsTestMatches',
  'state.isTestMatchesAllInfoSupplied',
  function() {
    if (this.get('currentUser.isAdmin')) {
      // admins don't need to verify testMatches
      return;
    }

    if (parseInt(this.get('state.updatedBudgetLowValue')) > parseInt(this.get('state.updatedBudgetHighValue'))) {
      let errors = {
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPENSATION_MIN]: 'Minimum compensation cannot be greater than maximum compensation.'
      };

      this.set('state.errors', errors);

      this.clearTestMatchesState(true);
    } else {
      let errors = this.get('state.errors');
      if (errors?.[ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.COMPENSATION_MIN] && Object.keys(errors).length === 1) {
        errors = {};
        this.set('state.errors', errors);
      }
    }

    once(this, this._observeTestMatchesHandler);
  })),

  /** Determine whether to make a call to testMatches. */
  _observeTestMatchesHandler() {
    if (this.get('state.isEligibleForSelect')) {
      return;
    }

    if (!this.get('state.isTestMatchesAllInfoSupplied')) {
      return;
    }

    this.updateCurrentHashForTestMatches();

    if (this.get('state.isTestMatchesRequestInFlight')) {
      return;
    }

    if (!this.get('state.isNeedsTestMatches')) {
      return;
    }

    this._runTestMatches();
  },

  /**
   * Do the actual work of running test matches.
   */
  _runTestMatches() {
    this.clearTestMatchesState();
    this.set('state.errors', {});

    this.testMatches().then(() => {
      scheduleOnce('afterRender', this, function() {
        // call after to render to allow all promise state to resolve.
        if (this.get('state.isNeedsTestMatches') && this.get('state.isTestMatchesAllInfoSupplied')) {
          this._runTestMatches();
        }
      });
    }).catch(e => {
      let errors = e.errors || null;
      this.set('state.errors', errors);

      if (errors) {
        scrollToError(this, errors);
      }

      scheduleOnce('afterRender', this, function() {
        // call after to render to allow all promise state to resolve.
        if (this.get('state.isNeedsTestMatches') && this.get('state.isTestMatchesAllInfoSupplied')) {
          this._runTestMatches();
        }
      });
    });
  },

  updatedBudgetLowValue(value) {
    this.set('state.updatedBudgetLowValue', value);
  },

  updatedBudgetHighValue(value) {
    this.set('state.updatedBudgetHighValue', value);
  },

  updateClassificationIndustryValue(value) {
    this.set('state.updatedClassificationIndustryValue', value);
  },

  updateClassificationSubjectValue(value) {
    this.set('state.updatedClassificationSubjectValue', value);
  },

  addClassificationIndustry(classification) {
    const currentClassifications = this.state.updatedClassificationIndustries;
    if (currentClassifications.length < POSTING_CREATE__CLASSIFICATION_CATEGORY_LIMIT) {
      this.set('state.updatedClassificationIndustries', [
        ...currentClassifications,
        classification,
      ]);
    }

    this.set('state.updatedClassificationIndustryValue', '');
  },

  addClassificationSubject(classification) {
    const currentClassifications = this.state.updatedClassificationSubjects;
    if (currentClassifications.length < POSTING_CREATE__CLASSIFICATION_CATEGORY_LIMIT) {
      this.set('state.updatedClassificationSubjects', [
        ...currentClassifications,
        classification,
      ]);
    }

    this.set('state.updatedClassificationSubjectValue', '');
  },

  removedClassificationIndustry(removedClassification) {
    const updatedClassifications = this.state.updatedClassificationIndustries.filter((classification) => classification !== removedClassification);
    this.set('state.updatedClassificationIndustries', updatedClassifications);
  },

  removeClassificationSubject(removedClassification) {
    const updatedClassifications = this.state.updatedClassificationSubjects.filter((classification) => classification !== removedClassification);
    this.set('state.updatedClassificationSubjects', updatedClassifications);
  },

  /**
   * Update the candidate location state from the passed in location
   *
   * @param {Location} location
   * @private
   */
  updateCandidateLocation(location) {
    if (!location) {
      const globalLocation = {
        locationType: LOCATION.TYPES.GLOBAL,
      };

      this.set('state.updatedCandidateLocation', globalLocation);
      return;
    }

    this.set('state.updatedCandidateLocation', location);
  },

  /**
   * Update the deadline date state based on the current date selected by the user.
   */
  _updateDateRangeTypeFromDate() {

    if (this.get('state.isDeadlineDateExpired')) {
      this.set('state.errors', {
        [ASSIGNMENT_COMMAND_CONSTANTS.CREATE_ASSIGNMENT_PROPS.DEADLINE] : 'Choose a valid date in the future'
      });
    }
  },

  /**
   * Update the freelancer level state from the minimum rating.
   *
   * @param {Number} minRating    1-5
   * @private
   */
  _updateFreelancerLevelForMinRating(minRating) {
    let level = '';
    switch (minRating) {
      case 1:
      case 2:
        level = POSTING_CREATE_FREELANCER_LEVEL.BASIC;
        break;
      case 3:
        level = POSTING_CREATE_FREELANCER_LEVEL.STANDARD;
        break;
      case 4:
      case 5:
        level = POSTING_CREATE_FREELANCER_LEVEL.PREMIUM;
        break;
    }

    this.set('state.updatedFreelancerLevel', level);
  },

  /**
   * Given an assignment's candidate location model we will update it to use
   * the more specific model
   *
   * @param {Assignment} assignment
   */
  _updateCandidateLocationStateFromAssignment(assignment) {
    const { candidateLocation, location } = assignment;

    // get country model for candidate location
    if (candidateLocation.locationType === LOCATION.TYPES.COUNTRY) {
      const country = COUNTRY.getCountryModelForLocation(this.state.availableCountries, candidateLocation);
      this.set('state.updatedCandidateLocation', country);
    } else if (candidateLocation.locationType === LOCATION.TYPES.REGION) {
      const region = REGION.getRegionModelForLocation(this.state.availableRegions, candidateLocation);
      this.set('state.updatedCandidateLocation', region);
    } else {
      this.set('state.updatedCandidateLocation', candidateLocation ?? null);
    }

    const { candidateLocationModelType } = this.state;
    const locationsDifferent = (
      candidateLocationModelType === LOCATION.TYPES.COUNTRY
      || candidateLocationModelType === LOCATION.TYPES.REGION
    )
      ? location?.id !== candidateLocation?.id
      : location?.placeId !== candidateLocation?.placeId;

    const usingCandidateLocation = Boolean(candidateLocation) && locationsDifferent;

    this.set('state.usingCandidateLocation', usingCandidateLocation);
  },

  /**
   * Given an assignment's location model we will update it to use
   * the more specific model
   *
   * @param {Assignment} assignment
   */
  _updateLocationStateFromAssignment(assignment) {
    const { location } = assignment;

    // get country model for location
    if (location.locationType === LOCATION.TYPES.COUNTRY) {
      const country = COUNTRY.getCountryModelForLocation(this.state.availableCountries, location);
      this.set('state.updatedLocation', country);
      return;
    }

    // get region model for location
    if (location.locationType === LOCATION.TYPES.REGION) {
      const region = REGION.getRegionModelForLocation(this.state.availableRegions, location);
      this.set('state.updatedLocation', region);
      return;
    }

    this.set('state.updatedLocation', location ?? null);
  },

  /**
   * Update the location state from the passed in location
   *
   * @param {Location} location
   * @private
   */
  updateLocation(location) {
    this.set('state.updatedLocation', location ?? null);

    if (!this.state.usingCandidateLocation) {
      this.set('state.updatedCandidateLocation', null);
    }
  },

  /**
   * Update the rate type.
   *
   * @param {String} rateType Location of type Assignment.RATE_TYPE_*
   */
  updateRateType(rateType) {
    this.set('state.updatedRateType', rateType);
  },

  /**
   * Update our state from existing schedule itemsd.
   *
   * @param {Array<AssignmentSchedule>} scheduleItems
   * @private
   */
  _updateScheduleStateFromScheduleItems(scheduleItems) {
    const { scheduleItemTypeIsCustomMap } = this.state;

    const selectedScheduleItems = scheduleItems.map(({ type, description, date }) =>
      createScheduleItem(type, description, date, Boolean(scheduleItemTypeIsCustomMap[type]))
    );

    // only show field if data exists
    if (scheduleItems.length > 0) {
      this.setProperties({
        'state.updatedScheduleItems': selectedScheduleItems,
        'state.updatedHasKeyDates': true,
      });
    }
  },

  updateTitleValue(value) {
    this.set('state.updatedTitle', value);
  },

  /**
   * Update our state from existing schedule itemsd.
   *
   * @param {Array<AssignmentDeliverable>} deliverables
   * @private
   */
  _updateDeliverablesStateFromDeliverableItems(deliverables) {
    const selectedDeliverables = deliverables.map(({ description }) =>
      createDeliverable(description)
    );

    this.set('state.updatedDeliverables', selectedDeliverables);
  },

  /**
   * Update our state from existing services.
   *
   * @param {Ember.A<Service>} services
   * @private
   */
  _updateServiceStateFromServices(services) {
    let selectedServices = this.get('state.updatedSelectedServices');
    services.forEach(service => {
      selectedServices.addObject(service);
    });
  },

  _updateClassificationsStateFromAssignment({ classifications }) {
    const classificationsByCategory = classifications.reduce((accum, classification) => {
      if (!accum[classification.category]) {
        accum[classification.category] = [];
      }

      // create dropdown option
      accum[classification.category].push({
        key: classification.label,
        model: classification.id,
        category: classification.category,
      });

      return accum;
    }, []);

    this.setProperties({
      'state.updatedClassificationSubjects': classificationsByCategory[CLASSIFICATION_CATEGORIES.CATEGORY_SUBJECT] ?? [],
      'state.updatedClassificationIndustries': classificationsByCategory[CLASSIFICATION_CATEGORIES.CATEGORY_INDUSTRY] ?? [],
    });
  },

  /**
   * Given an existing assignment, populate our update state correctly.
   *
   * @param {Assignment} assignment
   * @param {Boolean} opt_isUpdateDeadline    True if we want to update the deadline from the assignment
   *                                          i.e. might not in the clone cases and others.
   * @private
   */
  _updateStateFromExistingAssignment(assignment, opt_isUpdateDeadline = true) {
    const moment = this.get('moment');

    let state = this.get('state'),
      estimatedDays = assignment.get('estimatedDays'),
      updatedEstimatedDaysTypeEnum,
      updatedEstimatedCustomDays = '',
      assignmentDeadline = assignment.get('deadline'),
      deadlineMoment = assignmentDeadline ?
        moment.moment(assignmentDeadline) :
        null;

    if (deadlineMoment) {
      /**
       * When this is loaded from the server, Ember will convert the deadline date into local time from
       * UTC time 23:59:59.  We need to convert it back into local time 23:59:59 which is what this code is doing.
       * Always get the UTC year, month, and date, and always use the last hour, min, sec of the day.
       */
      deadlineMoment = moment.moment([
        deadlineMoment.toDate().getUTCFullYear(),
        deadlineMoment.toDate().getUTCMonth(),
        deadlineMoment.toDate().getUTCDate(),
        23,
        59,
        59,
        0]);
    }

    // get estimated days.
    if (estimatedDays < POSTING_CREATE_MIN_WHOLE_DAY_COUNT) {
      updatedEstimatedDaysTypeEnum = `${POSTING_CREATE_ESTIMATED_DAY_TYPES.WHOLE_DAY}${POSTING_CREATE_MIN_WHOLE_DAY_COUNT}`;
    } else if (estimatedDays > POSTING_CREATE_ESTIMATED_WHOLE_DAY_COUNT) {
      updatedEstimatedDaysTypeEnum = POSTING_CREATE_ESTIMATED_DAY_TYPES.CUSTOM_NUMBER_OF_DAYS;
      updatedEstimatedCustomDays = assignment.get('estimatedDays');
    } else {
      updatedEstimatedDaysTypeEnum = `${POSTING_CREATE_ESTIMATED_DAY_TYPES.WHOLE_DAY}${estimatedDays}`;
    }

    let coverExpenses = '';
    if (assignment.get('isAllowsExpenses') != null) {
      coverExpenses = assignment.get('isAllowsExpenses') ?
        POSTING_CREATE_COVER_EXPENSES_TYPE.YES :
        POSTING_CREATE_COVER_EXPENSES_TYPE.NO;
    }

    let rateType = assignment.get('rateType');
    if (!rateType) {
      rateType = ASSIGNMENT.RATE_TYPE.FLAT;
    }

    let assignmentType = assignment.get('type');
    if (!assignmentType) {
      assignmentType = ASSIGNMENT.TYPES.STANDARD;
    }

    state.set('editingPosting', assignment);
    state.setProperties({
      isSelect: assignment.get('select'),
      updatedPostingType: assignmentType,
      updatedBudgetLowValue: assignment.get('compensationMin'),
      updatedBudgetHighValue: assignment.get('compensationMax'),
      updatedCoverExpenses: coverExpenses,
      updatedDescription: assignment.get('description'),
      updatedEstimatedCustomDays: updatedEstimatedCustomDays,
      updatedEstimatedDaysTypeEnum: updatedEstimatedDaysTypeEnum,
      updatedExamples: assignment.get('examples'),
      updatedCoverFees: assignment.sellerFeesCovered || false,
      updatedExpedite: assignment.expediteCommitment || false,
      updatedHasKeyDates: assignment.updatedHasKeyDates || false,
      updatedPrivacyLevel: assignment.get('privacyLevel'),
      updatedRateType: rateType,
      updatedTitle: htmlEntitiesDecode(assignment.get('title')),
      updatedCompanyTeam: assignment.companyTeam,
      updatedProduction: assignment.production,
    });

    if (deadlineMoment && opt_isUpdateDeadline) {
      state.set('updatedDateSelected', deadlineMoment);
      state.set('updatedDateRangeType', POSTING_CREATE_DATE_RANGE_TYPE.SELECT_A_DATE);
    }

    if (opt_isUpdateDeadline) {
      this._updateDateRangeTypeFromDate();
    }
    this._updateFreelancerLevelForMinRating(assignment.get('minRating'));
    this._updateLocationStateFromAssignment(assignment);
    this._updateCandidateLocationStateFromAssignment(assignment);
    this._updateServiceStateFromServices(assignment.get('services'));
    this._updateClassificationsStateFromAssignment(assignment);
    this._updateDeliverablesStateFromDeliverableItems(assignment.assignmentDeliverables);
    this._updateScheduleStateFromScheduleItems(assignment.assignmentSchedules);
  }
});
